// src/components/MemberTable/MemberTable.jsx

import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import './MemberTable.css';
import Spinner from '../Common/Spinner';
import SearchBar from './SearchBar';
import ActionButton from '../Common/ActionButton';
import PaginationControls from './PaginationControls';
import Table from './Table';
import ExportModal from './ExportModal';
import { useTranslation } from 'react-i18next';
import { useLanguageSettings } from '../Common/LanguageSettingsContext';
import { useColorSettings } from '../Common/ColorSettingsContext';
import { DataContext } from '../../DataContext';

const MemberTable = ({
  onEdit,
  onDelete,
  onAddMember,
  onSort,
  sortField,
  sortOrder,
  searchTerm,
  onSearchChange,
  userRole,
  onSendMessage,
  onExport,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguageSettings();
  const { appSettings } = useColorSettings();

  const {
    sortedRecords,
    allRecordsLoading,
    allRecordsError,
    itemsPerPage: contextItemsPerPage, // Renamed to avoid confusion
    onItemsPerPageChange: contextOnItemsPerPageChange,
  } = useContext(DataContext);

  // Local pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(contextItemsPerPage);

  // Debounce search input to optimize performance
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Filter records based on debouncedSearchTerm
  const filteredRecords = useMemo(() => {
    if (!debouncedSearchTerm) return sortedRecords || [];

    const lowercasedTerm = debouncedSearchTerm.toLowerCase();

    return (sortedRecords || []).filter((record) => {
      const fullName = `${record.nombre} ${record.apellido}`.toLowerCase();
      const city = record.ciudad ? record.ciudad.toLowerCase() : '';
      const state = record.estado ? record.estado.toLowerCase() : '';
      const phone = record.telefono ? record.telefono.toLowerCase() : '';
      const email = record.correo ? record.correo.toLowerCase() : '';

      return (
        fullName.includes(lowercasedTerm) ||
        city.includes(lowercasedTerm) ||
        state.includes(lowercasedTerm) ||
        phone.includes(lowercasedTerm) ||
        email.includes(lowercasedTerm)
      );
    });
  }, [debouncedSearchTerm, sortedRecords]);

  // Calculate total pages based on filteredRecords
  const totalPages = useMemo(() => {
    return Math.ceil(filteredRecords.length / itemsPerPage) || 1;
  }, [filteredRecords.length, itemsPerPage]);

  // Ensure currentPage is within the new totalPages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Paginate the filtered records
  const paginatedRecords = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return filteredRecords.slice(startIdx, endIdx);
  }, [filteredRecords, currentPage, itemsPerPage]);

  // Determine if it's the initial loading phase
  const isInitialLoading =
    allRecordsLoading && (sortedRecords == null || sortedRecords.length === 0);

  // State for controlling the export modal visibility
  const [showExportModal, setShowExportModal] = useState(false);

  if (allRecordsError) {
    return <div>Error fetching records: {allRecordsError.message}</div>;
  }

  return (
    <>
      <div className="member-table-container">
        <div className="member-table-header">
          <div className="header-top">
            <SearchBar searchTerm={searchTerm} onSearchChange={onSearchChange} />
            {/* Add Member Button */}
            <ActionButton
              onClick={onAddMember}
              label={t('memberTable.addNewMember')}
              icon={'faPlus'}
              isMobile={false}
              colorType="primary"
            />
            {/* Export Button */}
            {/*<ActionButton
              onClick={() => setShowExportModal(true)}
              label={t('memberTable.export')}
              icon={'faDownload'}
              isMobile={false}
              colorType="primary"
            />*/}
          </div>
        </div>

        <div className="record-count" aria-live="polite">
          {filteredRecords.length === 1
            ? t('memberTable.recordsFound', { count: filteredRecords.length })
            : t('memberTable.recordsFound_plural', { count: filteredRecords.length })}
        </div>

        {/* Pagination Controls (Top) */}
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          onPageClick={(page) => setCurrentPage(page)}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1); // Reset to first page on items per page change
          }}
        />

        <Table
          data={paginatedRecords}
          onEdit={onEdit}
          onDelete={onDelete}
          onSendMessage={onSendMessage}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
          userRole={userRole}
          isMobile={true}
        />

        {/* Spinner while loading more data in the background */}
        {allRecordsLoading && sortedRecords.length > 0 && <Spinner fullScreen={false} />}

        {/* Pagination Controls (Bottom) */}
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          onPageClick={(page) => setCurrentPage(page)}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1); // Reset to first page on items per page change
          }}
        />
      </div>

      {/* Export Modal */}
      <ExportModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={onExport}
      />
    </>
  );
};

MemberTable.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddMember: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  userRole: PropTypes.string.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
};

MemberTable.defaultProps = {
  sortField: 'fechaNacimiento',
  sortOrder: 'asc',
};

export default MemberTable;
